import React, { useEffect, useMemo } from "react"

import { useSearchParams } from "react-router-dom"

import { FeatureFlagsExistenceLogicalOperator } from "@common-shared/config/featureFlags.config.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import {
    CHECKER_CONFIG,
    CoverageCheckerMode,
} from "../../config/checker.config"
import { FEATURE_FLAGS_CONFIG } from "../../config/featureFlags.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { FeatureFlagsHelper } from "../../helpers/featureFlags.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { checkerSelector } from "../../store/selectors/checker.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { checkerToggleRunningType } from "../../store/slices/checker.slice"
import type { ICheckerRunningTypeTab } from "../../types/objects.types.ts"

export const RunningTypeSelectorComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()

    const runtimeState = useAppSelector(runtimeSelector)

    const { checkerMode: selectedCheckerMode } = useAppSelector(checkerSelector)

    const availableTabs = useMemo<ICheckerRunningTypeTab[]>(() => {
        return CHECKER_CONFIG.checkerRunningTypeTabs.filter(item => {
            if (!runtimeState.enabledFeatureFlags) {
                return false
            }

            return item?.featureFlag
                ? FeatureFlagsHelper.isFeatureFlagEnabled(
                      runtimeState.enabledFeatureFlags,
                      item.featureFlag,
                      item.featureFlagExistenceOperator ||
                          FeatureFlagsExistenceLogicalOperator.OR,
                  )
                : true
        })
    }, [runtimeState.enabledFeatureFlags])

    // Check if manual or csv flow was requested by default from url
    useEffect(() => {
        const checkerMode = searchParams.get(
            GENERAL_CONFIG.urlSearchParamsKeys.checkerMode,
        )

        // If some random value was provided
        if (
            !checkerMode ||
            !(Object.values(CoverageCheckerMode) as string[]).includes(
                checkerMode,
            )
        ) {
            return
        }

        onSelected(checkerMode as CoverageCheckerMode)
    }, [searchParams])

    // If only one tab is available, do not show the selector
    // And select that type in state
    useEffect(() => {
        if (!availableTabs.length || !runtimeState.enabledFeatureFlags) {
            return
        }

        let isSelectedTabAllowed = false

        switch (selectedCheckerMode) {
            case CoverageCheckerMode.CSV:
                isSelectedTabAllowed = FeatureFlagsHelper.isFeatureFlagEnabled(
                    runtimeState.enabledFeatureFlags,
                    FEATURE_FLAGS_CONFIG.bulkCheckerFeatureFlags,
                )

                break

            case CoverageCheckerMode.MANUAL:
                isSelectedTabAllowed = FeatureFlagsHelper.isFeatureFlagEnabled(
                    runtimeState.enabledFeatureFlags,
                    FEATURE_FLAGS_CONFIG.manualCheckerFeatureFlags,
                )

                break
        }

        if (!isSelectedTabAllowed) {
            onSelected(availableTabs[0].key)
        }
    }, [runtimeState.enabledFeatureFlags, availableTabs, selectedCheckerMode])

    const onResetSearchParams = () => {
        // Remove results search params flag - it's added when triggering new results check
        searchParams.delete(GENERAL_CONFIG.urlSearchParamsKeys.results)
        setSearchParams(searchParams)
    }

    const onSelected = (value: CoverageCheckerMode) => {
        dispatch(
            checkerToggleRunningType({
                checkerMode: value,
            }),
        )

        onResetSearchParams()
    }

    return availableTabs.length > 1 ? (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <div className="flex items-center md:block">
                <div className="flex-1 flex items-center flex-wrap md:block">
                    {availableTabs.map((tab, index) => {
                        const isSelected = tab.key === selectedCheckerMode

                        return (
                            <div
                                key={index}
                                data-produktly={`checker-mode-${tab.key}`}
                                className={`
                                    relative mr-[8px] px-[12px] pt-[14px] pb-[10px] rounded-[6px]
                                    md:mt-[8px]
                                    ${
                                        isSelected
                                            ? "bg-brand-primary"
                                            : "border border-solid border-brand-warmShadow cursor-pointer"
                                    }
                                `}
                                onClick={
                                    !isSelected
                                        ? () => {
                                              onSelected(tab.key)
                                          }
                                        : undefined
                                }
                            >
                                <PrimaryText
                                    typography="buttonText"
                                    color={
                                        isSelected
                                            ? NIRVANA_COLORS.brand.white
                                            : NIRVANA_COLORS.brand.primary
                                    }
                                >
                                    {tab.label}
                                </PrimaryText>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    ) : null
}
